import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Box,
  Divider,
} from '@chakra-ui/react';
import { X } from 'lucide-react';
import type { CleanupAction } from './control-panel-dummy-data';

const TYPE_BADGE_COLOR: Record<CleanupAction['type'], string> = {
  cleanup: 'teal',
  optimization: 'purple',
};

const TYPE_LABEL: Record<CleanupAction['type'], string> = {
  cleanup: 'Clean up',
  optimization: 'Optimization',
};

interface ActionsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  actions: CleanupAction[];
  onRemoveAction: (id: string) => void;
}

export const ActionsDetailModal: React.FC<ActionsDetailModalProps> = ({
  isOpen,
  onClose,
  actions,
  onRemoveAction,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Selected Actions ({actions.length})</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {actions.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No actions selected.
            </Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {actions.map((action, index) => (
                <React.Fragment key={action.id}>
                  <HStack align="flex-start" spacing={3}>
                    <Box flex={1}>
                      <HStack mb={1} spacing={2} flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {action.name}
                        </Text>
                        <Badge colorScheme={TYPE_BADGE_COLOR[action.type]} fontSize="xs">
                          {TYPE_LABEL[action.type]}
                        </Badge>
                        <Badge colorScheme="purple" variant="outline" fontSize="xs">
                          {action.category}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">
                        {action.description}
                      </Text>
                    </Box>
                    <IconButton
                      aria-label={`Remove action ${action.name}`}
                      icon={<X size={14} />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      flexShrink={0}
                      onClick={() => onRemoveAction(action.id)}
                    />
                  </HStack>
                  {index < actions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
